import {
  type SelectOption,
  Accordion,
  AccordionVariant,
  Button,
  ButtonVariant,
  Input,
  InputGroup,
  Placement,
  RadioToggleGroup,
  toast,
} from '@aus-platform/design-system';
import { useAppSelector } from 'app/hooks';
import { isEmpty, isNil, snakeCase, xor } from 'lodash';
import { MapTool } from 'map-3d/components/map-3d-container/map-3d-tools';
import { LineEventType } from 'map-3d/shared';
import {
  selectActiveWorkspaceLayersByFeatureType,
  selectMap3dDataset,
  selectMap3DState,
  selectMap3dWorkspace,
  setCurrentActiveMapTool,
} from 'map-3d/shared/map-3d-slices';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  type AddHaulRoadPayload,
  type FeatureResponseData,
  FeatureCountLabel,
  FeatureGeometryType,
  FeatureType,
  handleResponseErrorMessage,
  useAccessTagList,
  useAddHaulRoad,
  useFeatureList,
  useHaulRoadTypes,
} from 'shared/api';
import { GlobalContext } from 'shared/context';
import { preventNonInteger, preventNonNumber } from 'shared/helpers';
import { useInputFields } from 'shared/hooks';
import { areAccessTagsRequiredForLayer } from 'src/sites/helpers';
import {
  type WorkspaceLayer,
  type WorkspaceLayerListObj,
  MarkArea,
  MarkAreaDrawingToolType,
  MarkAreaToggle,
} from '../../../shared';
import { setVectorLayerVisibility } from '../../map-3d-workspace';
import { getEligibleLayerOptions } from '../../map-3d-workspace/shared/helpers';
import { haulRoadAnalyticsTabs } from '../enums';
import { haulRoadLayersType, hraInitialState } from './constant';
import { FormErrorMessages, HaulRoadLayerType } from './enums';
import { hraValidator } from './hra-validator';
import { HRAViewOutputs } from './hra-view-outputs';
import type { HRAInputFieldType } from './types';

export const HRAGenerateForm = ({ setActiveTabKey }) => {
  // Contexts.
  const { loggedUser } = useContext(GlobalContext);

  // States.
  const [currentMarkedArea, setCurrentMarkedArea] = useState(
    MarkArea.LayerFromWorkspace,
  );
  const [isDrawLineToolEnabled, setIsDrawLineToolEnabled] =
    useState<boolean>(false);
  const [hasLines, setHasLines] = useState<boolean>(false);
  const [currentHRLayerType, setCurrentHRLayerType] = useState(
    HaulRoadLayerType.EdgesLayer,
  );
  const [haulRoadTypeOptions, setHaulRoadTypeOptions] = useState<
    SelectOption[]
  >([]);
  const [showGenerateHaulRoadForm, setShowGenerateForm] = useState(true);
  const [accessTagList, setAccessTagList] = useState<SelectOption[]>([]);

  // Hooks.
  const {
    values,
    setValues,
    names,
    errors,
    onChange,
    onBlur,
    onFocus,
    setErrors,
    inputHasError,
    setOptional,
    getOptionalFields,
  } = useInputFields<HRAInputFieldType>(hraInitialState, hraValidator);

  // Selectors.
  const { selectedTerrainIteration } = useAppSelector(selectMap3dDataset);
  const { aoiManager } = useAppSelector(selectMap3DState);

  const dispatch = useDispatch();
  const { workspaceLayers } = useAppSelector(selectMap3dWorkspace);
  const activeLineLayers = useAppSelector((state) =>
    selectActiveWorkspaceLayersByFeatureType(state, FeatureGeometryType.Line),
  );
  const activePolygonLayers = useAppSelector((state) =>
    selectActiveWorkspaceLayersByFeatureType(
      state,
      FeatureGeometryType.Polygon,
    ),
  );
  const polygonLayersSelectOption: SelectOption<WorkspaceLayer>[] =
    activePolygonLayers.map((activeLayer) => ({
      label: activeLayer.name,
      value: activeLayer,
    }));

  const singleLineLayersSelectOptions: SelectOption<WorkspaceLayer>[] =
    getEligibleLayerOptions(activeLineLayers, FeatureCountLabel.Lines, 1);

  const twoLineLayersSelectOptions: SelectOption<WorkspaceLayer>[] =
    getEligibleLayerOptions(activeLineLayers, FeatureCountLabel.Lines, 2);

  const { layerAccessControl: accessControl } =
    useAppSelector(selectMap3dWorkspace);

  // Drawing tools.
  const lineTool = aoiManager?.lineTool;
  const polygonTool = aoiManager?.polygonTool;

  const layerAccessTagsRequired = areAccessTagsRequiredForLayer(
    accessControl.accessType,
    loggedUser,
  );

  const isGenerateHRADisabled =
    inputHasError(getOptionalFields()) ||
    (currentMarkedArea === MarkArea.Draw && !hasLines);

  // UseMemo.
  const visibleWorkspaceLayers: WorkspaceLayerListObj = useMemo(
    () =>
      Object.keys(workspaceLayers).reduce((visibleLayers, layerId) => {
        if (workspaceLayers[layerId].show) {
          visibleLayers[layerId] = workspaceLayers[layerId];
          return visibleLayers;
        }
        return visibleLayers;
      }, {}),
    [workspaceLayers],
  );

  // Api.
  const {
    data: featuresResponse,
    isFetching: isLoadingFeaturesResponse,
    isSuccess: isSuccessFeaturesResponse,
    isError: isErrorFeaturesResponse,
    error: featuresResponseError,
    refetch: refetchFeatures,
  } = useFeatureList(
    values.workspaceLayer?.value.id ?? null,
    FeatureType.LineString,
    false,
  );

  const {
    data: polygonFeaturesResponse,
    isFetching: isLoadingPolygonFeaturesResponse,
    isSuccess: isSuccessPolygonFeaturesResponse,
    isError: isErrorPolygonFeaturesResponse,
    error: polygonFeaturesResponseError,
    refetch: refetchPolygonFeatures,
  } = useFeatureList(
    values.haulRoadMedianLayer?.value.id ?? null,
    FeatureType.Polygon,
    false,
  );

  const {
    data: haulRoadTypeResponse,
    error: haulRoadTypeResponseError,
    isError: isErrorHaulRoadTypeResponse,
    isSuccess: isSuccessHaulRoadType,
    isLoading: isLoadingHaulRoadType,
  } = useHaulRoadTypes();

  const {
    mutate: sendAddHaulRoadRequest,
    error: addHaulRoadResponseError,
    isError: isErrorAddHaulRoadResponse,
    isPending: isAddHaulRoadPending,
    isSuccess: isSuccessAddHaulRoad,
  } = useAddHaulRoad();

  const {
    data: accessTagsListResponse,
    isSuccess: isAccessTagsListSuccess,
    isLoading: isAccessTagsListLoading,
    isError: isAccessTagsListError,
    error: accessTagsListError,
  } = useAccessTagList();

  const lineLayersSelectOptions =
    currentHRLayerType === HaulRoadLayerType.CenterLineLayer
      ? singleLineLayersSelectOptions
      : twoLineLayersSelectOptions;

  // useEffects.
  useEffect(() => {
    // Set initial optional fields.
    setOptional((prev) => ({
      ...prev,
      haulRoadMedianLayer: true,
      workspaceLayer: false,
      haulRoadLayersType: true,
      accessTags: true,
    }));
    validateHaulRoadLayerSelection();
    setVectorLayerVisibility(workspaceLayers, false);

    return () => {
      setVectorLayerVisibility(workspaceLayers, true);
    };
  }, []);

  useEffect(() => {
    setOptional((prev) => ({
      ...prev,
      accessTags: !layerAccessTagsRequired,
    }));
  }, [layerAccessTagsRequired]);

  useEffect(() => {
    if (isNil(lineTool) || !isDrawLineToolEnabled) {
      return;
    }

    const onLineCreated = () => {
      setHasLines(true);
    };

    lineTool.addEventListener(LineEventType.LineCreated, onLineCreated);

    return () => {
      lineTool.removeEventListener(LineEventType.LineCreated, onLineCreated);
    };
  }, [lineTool, isDrawLineToolEnabled]);

  useEffect(() => {
    handleResponseErrorMessage(isErrorFeaturesResponse, featuresResponseError);
  }, [isErrorFeaturesResponse, featuresResponseError]);

  useEffect(() => {
    handleResponseErrorMessage(
      isErrorHaulRoadTypeResponse,
      haulRoadTypeResponseError,
    );
  }, [haulRoadTypeResponseError, isErrorHaulRoadTypeResponse]);

  useEffect(() => {
    if (isSuccessHaulRoadType && haulRoadTypeResponse) {
      setHaulRoadTypeOptions(
        haulRoadTypeResponse.map((haulRoadType) => ({
          label: haulRoadType.name,
          value: haulRoadType.id,
        })),
      );
    }
  }, [haulRoadTypeResponse, isSuccessHaulRoadType]);

  useEffect(() => {
    handleResponseErrorMessage(
      isErrorAddHaulRoadResponse,
      addHaulRoadResponseError,
    );
  }, [addHaulRoadResponseError, isErrorAddHaulRoadResponse]);

  useEffect(() => {
    handleResponseErrorMessage(
      isErrorPolygonFeaturesResponse,
      polygonFeaturesResponseError,
    );
  }, [isErrorPolygonFeaturesResponse, polygonFeaturesResponseError]);

  useEffect(() => {
    if (isSuccessAddHaulRoad) {
      setShowGenerateForm(false);
      toast.success('Haul road generation started');
    }
  }, [isSuccessAddHaulRoad]);

  useEffect(() => {
    if (isSuccessFeaturesResponse && !isNil(featuresResponse)) {
      loadLinesFromWorkspace(featuresResponse.data.features);
    }
  }, [isSuccessFeaturesResponse, featuresResponse]);

  useEffect(() => {
    if (isSuccessPolygonFeaturesResponse && !isNil(polygonFeaturesResponse)) {
      loadPolygonFromWorkspace(polygonFeaturesResponse.data.features);
    }
  }, [isSuccessPolygonFeaturesResponse, polygonFeaturesResponse]);

  useEffect(() => {
    if (isNil(values.workspaceLayer)) {
      return;
    }

    refetchFeatures();
    deleteHRLinesByMarkedArea(MarkArea.Draw);

    const features = values.workspaceLayer?.value.features;
    const selectedHRLinesIds: Array<string> | undefined = features?.map(
      (feature) => {
        return feature.id;
      },
    );
    deleteHRLinesByMarkedArea(MarkArea.LayerFromWorkspace, selectedHRLinesIds);
  }, [values.workspaceLayer]);

  useEffect(() => {
    if (isNil(values.haulRoadMedianLayer)) {
      return;
    }
    refetchPolygonFeatures();
    deleteHRPolygonsByMarkedArea(MarkArea.LayerFromWorkspace);
  }, [values.haulRoadMedianLayer]);

  useEffect(() => {
    // Show the workspace layers when moved away from the HRA tab.
    return () => {
      if (currentMarkedArea === MarkArea.Draw) {
        setVectorLayerVisibility(visibleWorkspaceLayers, true);
      } else {
        setVectorLayerVisibility(workspaceLayers, true);
      }
    };
  }, [workspaceLayers]);

  useEffect(() => {
    if (isAccessTagsListSuccess && accessTagsListResponse) {
      const accessTags = accessTagsListResponse.data.list.map((accessTag) => {
        return {
          label: accessTag.name,
          value: accessTag.id,
        };
      });
      setAccessTagList(accessTags);
    }

    handleResponseErrorMessage(isAccessTagsListError, accessTagsListError);
  }, [accessTagsListResponse, accessTagsListError]);

  // Helpers.
  const validateHaulRoadLayerSelection = () => {
    if (
      values.haulRoadLayersType.value === HaulRoadLayerType.CenterLineLayer &&
      isEmpty(singleLineLayersSelectOptions)
    ) {
      setErrors({
        ...errors,
        workspaceLayer: FormErrorMessages.NoActiveCenterLineLayer,
      });
    }
    if (
      values.haulRoadLayersType.value === HaulRoadLayerType.EdgesLayer &&
      isEmpty(twoLineLayersSelectOptions)
    ) {
      setErrors({
        ...errors,
        workspaceLayer: FormErrorMessages.NoActiveEdgeLineLayer,
      });
    }
  };

  // Handlers
  const onSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();

    if (
      !lineTool &&
      !values.workspaceLayer?.value.id &&
      !inputHasError(getOptionalFields())
    ) {
      return;
    }

    const {
      haulRoadName,
      haulRoadType,
      vehicleWidth,
      chainageInterval,
      vehicleTyreHeight,
      haulRoadLayersType,
      workspaceLayer,
      haulRoadMedianLayer,
      accessTags,
    } = values;

    const haulRoadPayLoad: AddHaulRoadPayload = {
      name: haulRoadName,
      type: haulRoadType?.value,
      vehicleWidth: vehicleWidth,
      vehicleTyreHeight: vehicleTyreHeight,
      chainageInterval: chainageInterval,
      iteration: selectedTerrainIteration?.id,
      layerAccessTags: accessTags?.map((accessTag) => accessTag.value) ?? [],
    };

    if (!isNil(workspaceLayer)) {
      if (haulRoadLayersType.value === HaulRoadLayerType.CenterLineLayer) {
        haulRoadPayLoad.centerLineLayer = workspaceLayer.value.id;
      } else {
        haulRoadPayLoad.edgeLayer = workspaceLayer.value.id;
        if (haulRoadMedianLayer?.value) {
          haulRoadPayLoad.mediansLayer = haulRoadMedianLayer.value.id;
        }
      }
    } else {
      haulRoadPayLoad.smartLineWkt =
        lineTool?.exportCurrentLinesToWKT()?.[0]?.wkt ?? '';
    }

    sendAddHaulRoadRequest(haulRoadPayLoad);
  };

  const resetDrawingHandler = () => {
    setHasLines(false);
    if (lineTool?.isAnyLineDrawn) {
      deleteHRLinesByMarkedArea(MarkArea.Draw);
      lineTool.deactivate();
      lineTool.activate({ haulRoadLines: MarkArea.Draw }, {}, true);
    }
  };

  const onRadioChange = (event) => {
    const newLayerType = event.target.title;

    haulRoadLayersType.forEach((layerType) => {
      if (newLayerType === layerType.value) {
        setCurrentHRLayerType(newLayerType);
        setValues({
          ...values,
          haulRoadLayersType: layerType,
          workspaceLayer: null,
          haulRoadMedianLayer: null,
        });
      }
      deleteHRPolygonsByMarkedArea(MarkArea.LayerFromWorkspace);
      deleteHRLinesByMarkedArea(MarkArea.LayerFromWorkspace);
    });

    if (newLayerType === HaulRoadLayerType.CenterLineLayer) {
      setErrors({
        ...errors,
        workspaceLayer: isEmpty(singleLineLayersSelectOptions)
          ? FormErrorMessages.NoActiveCenterLineLayer
          : '',
      });
    } else if (newLayerType === HaulRoadLayerType.EdgesLayer) {
      setErrors({
        ...errors,
        workspaceLayer: isEmpty(twoLineLayersSelectOptions)
          ? FormErrorMessages.NoActiveEdgeLineLayer
          : '',
      });
    }
  };

  const startDrawingHandler = () => {
    lineTool?.activate({ haulRoadLines: MarkArea.Draw }, {}, true);
    dispatch(setCurrentActiveMapTool(MapTool.None));
    deleteHRLinesByMarkedArea(MarkArea.LayerFromWorkspace);
    deleteHRPolygonsByMarkedArea(MarkArea.LayerFromWorkspace);
    setVectorLayerVisibility(workspaceLayers, false);
    setIsDrawLineToolEnabled(true);
    setValues({ ...values, workspaceLayer: null, haulRoadMedianLayer: null });
  };

  const endDrawingHandler = () => {
    lineTool?.deactivate();
    setIsDrawLineToolEnabled(false);
  };

  const onToggleChange = (toggledValue: string) => {
    // TODO: Refactor toggle switch to accept type to return type with onChange value.
    const toggleValueAsEnum = snakeCase(toggledValue) as MarkArea;

    if (toggleValueAsEnum === MarkArea.Draw) {
      deleteHRLinesByMarkedArea(MarkArea.LayerFromWorkspace);
      deleteHRPolygonsByMarkedArea(MarkArea.LayerFromWorkspace);
      setVectorLayerVisibility(visibleWorkspaceLayers, true);

      setErrors({ ...errors, workspaceLayer: '' });

      setValues({ ...values, workspaceLayer: null });

      setOptional((prev) => ({
        ...prev,
        workspaceLayer: true,
        haulRoadLayersType: true,
      }));
    } else {
      setVectorLayerVisibility(workspaceLayers, false);
      deleteHRLinesByMarkedArea(MarkArea.Draw);
      setHasLines(false);
      lineTool?.deactivate();

      setOptional((prev) => ({
        ...prev,
        workspaceLayer: false,
        haulRoadLayersType: false,
      }));
      validateHaulRoadLayerSelection();
    }

    setIsDrawLineToolEnabled(false);
    setCurrentMarkedArea(toggleValueAsEnum);
  };

  const loadLinesFromWorkspace = (featureResponse: FeatureResponseData[]) => {
    for (const feature of featureResponse) {
      if (feature.type === FeatureType.LineString) {
        lineTool?.importWKT(
          feature.geometry,
          feature.id,
          { haulRoadLines: MarkArea.LayerFromWorkspace },
          feature.name,
        );
      }
    }
  };

  const loadPolygonFromWorkspace = (featureResponse: FeatureResponseData[]) => {
    for (const feature of featureResponse) {
      if (feature.type === FeatureType.Polygon) {
        polygonTool?.importWKT(
          feature.geometry,
          feature.id,
          { haulRoadPolygons: MarkArea.LayerFromWorkspace },
          feature.name,
        );
      }
    }
  };

  const onClickNewHaulRoad = () => {
    setShowGenerateForm(true);
    setValues({
      ...values,
      haulRoadName: '',
      workspaceLayer: null,
      haulRoadMedianLayer: null,
    });
  };

  const onClickViewOutputs = () => {
    setActiveTabKey(haulRoadAnalyticsTabs.Outputs);
  };

  const deleteHRLinesByMarkedArea = (
    markArea: MarkArea,
    excludeLines?: string[],
  ) => {
    const hrLines = lineTool
      ?.getLinesByProperty('haulRoadLines', markArea)
      .map((hrLine) => {
        return hrLine.id;
      });

    const linesToDelete = xor(hrLines, excludeLines);

    for (const line of linesToDelete) {
      lineTool?.deleteById(line);
    }
  };

  const deleteHRPolygonsByMarkedArea = (markArea: MarkArea) => {
    const hrPolygons = polygonTool
      ?.getPolygonByProperty('haulRoadPolygons', markArea)
      .map((polygon) => {
        return polygon.id;
      });

    if (isNil(hrPolygons)) {
      return;
    }

    for (const polygon of hrPolygons) {
      polygonTool?.deleteById(polygon);
    }
  };

  // Components.
  const MarkAreaHeader = () => (
    <Input.Label
      info="Use Smart Line to draw origin destination, midpoints and connecting lines"
      infoPlacement={Placement.Top}
      tooltipClassname="destination-points-tooltip"
      isRequired
    >
      Mark Origin & Destination Points
    </Input.Label>
  );

  const SelectLayerFromWorkspaceTypes = () => (
    <InputGroup className="hra-generate-form__layers-toggle input-field--bold">
      <Input.Label isRequired>Haul Road Layers</Input.Label>
      <RadioToggleGroup
        radioOptions={haulRoadLayersType}
        onChange={onRadioChange}
        checked={(option) => {
          return values.haulRoadLayersType.value === option.value;
        }}
      />
    </InputGroup>
  );

  return showGenerateHaulRoadForm ? (
    <form className="hra-generate-form-container" onSubmit={onSubmit}>
      <div className="input-field-container map-3d-sidebar-content">
        <InputGroup className="input-field--bold">
          <Input.Label>Active Terrain</Input.Label>
          <Input.Text disabled value={selectedTerrainIteration?.name} />
        </InputGroup>
        <InputGroup className="input-field--bold">
          <Input.Label isRequired>Haul Road Name</Input.Label>
          <Input.Text
            placeholder="Haul Road Name"
            value={values.haulRoadName}
            name={names.haulRoadName}
            error={errors.haulRoadName}
            isInvalid={!!errors.haulRoadName}
            disabled={isDrawLineToolEnabled}
            required
            {...{ onChange, onBlur, onFocus }}
          />
        </InputGroup>
        <InputGroup className="input-field--bold">
          <Input.Label isRequired={layerAccessTagsRequired}>
            Access Tags
          </Input.Label>
          <Input.Select
            placeholder="Select Access Tags"
            options={accessTagList}
            isMulti={true}
            name={names.accessTags}
            value={values.accessTags}
            error={errors.accessTags}
            isLoading={isAccessTagsListLoading}
            onChange={(selectedAccessTags) => {
              setValues({ ...values, accessTags: selectedAccessTags });
            }}
            {...{ onBlur, onFocus }}
          />
        </InputGroup>

        <MarkAreaToggle
          headerComponent={<MarkAreaHeader />}
          currentMarkedArea={currentMarkedArea}
          onToggleChange={onToggleChange}
          drawingHandlers={{
            endDrawingHandler,
            startDrawingHandler,
            resetDrawingHandler,
          }}
          isDrawingToolEnabled={isDrawLineToolEnabled}
          workspaceLayerError={errors.workspaceLayer}
          typeOfLayerComponent={<SelectLayerFromWorkspaceTypes />}
          selectLayerSelectProps={{
            options: lineLayersSelectOptions,
            value: values.workspaceLayer,
            isDisabled: isEmpty(lineLayersSelectOptions),
            name: names.workspaceLayer,
            isLoading: isLoadingFeaturesResponse,
            onChange: (selectedLayer: SelectOption<WorkspaceLayer>) => {
              setValues({ ...values, workspaceLayer: selectedLayer });
            },
            onBlur,
            onFocus,
          }}
          drawingType={MarkAreaDrawingToolType.Line}
          isRequired
        />

        <InputGroup className="hra-generate-form__drawing input-field--bold">
          <Input.Label>Width, Gradient & Berm Analysis</Input.Label>
        </InputGroup>
        <InputGroup>
          <Input.Label isRequired>Haul Road Type</Input.Label>
          <Input.Select
            options={haulRoadTypeOptions}
            value={values.haulRoadType}
            name={names.haulRoadType}
            isLoading={isLoadingHaulRoadType}
            placeholder="Haul Road Type"
            onChange={(haulRoadType) => {
              setValues({ ...values, haulRoadType });
            }}
            error={errors.haulRoadType}
            {...{ onBlur }}
          />
        </InputGroup>

        <InputGroup>
          <Input.Label
            info="Enter a positive number up to 100m"
            infoPlacement={Placement.TopStart}
            tooltipClassname="width-gradient-analysis-tooltip"
            isRequired
          >
            Vehicle Width (m)
          </Input.Label>
          <Input.Number
            value={values.vehicleWidth}
            name={names.vehicleWidth}
            isInvalid={!!errors.vehicleWidth}
            min={1}
            max={100}
            onKeyDown={preventNonNumber}
            error={errors.vehicleWidth}
            disabled={isDrawLineToolEnabled}
            placeholder="Vehicle Width"
            {...{ onChange, onBlur, onFocus }}
          />
        </InputGroup>
        <InputGroup>
          <Input.Label
            info="Enter a positive number up to 12m"
            infoPlacement={Placement.TopStart}
            tooltipClassname="width-gradient-analysis-tooltip"
            isRequired
          >
            Vehicle Tyre Height (m)
          </Input.Label>
          <Input.Number
            value={values.vehicleTyreHeight}
            name={names.vehicleTyreHeight}
            isInvalid={!!errors.vehicleTyreHeight}
            min={0.01}
            max={12}
            onKeyDown={preventNonNumber}
            error={errors.vehicleTyreHeight}
            disabled={isDrawLineToolEnabled}
            placeholder="Vehicle Tyre Height"
            {...{ onChange, onBlur, onFocus }}
          />
        </InputGroup>
        <InputGroup>
          <Input.Label
            info="Enter a positive number up to 500m"
            infoPlacement={Placement.BottomStart}
            tooltipClassname="width-gradient-analysis-tooltip"
            isRequired
          >
            Chainage Interval (m)
          </Input.Label>
          <Input.Number
            value={values.chainageInterval}
            name={names.chainageInterval}
            isInvalid={!!errors.chainageInterval}
            min={1}
            max={500}
            onKeyDown={preventNonInteger}
            error={errors.chainageInterval}
            disabled={isDrawLineToolEnabled}
            placeholder="Chainage Interval"
            {...{ onChange, onBlur, onFocus }}
          />
        </InputGroup>

        {currentMarkedArea === MarkArea.LayerFromWorkspace &&
          currentHRLayerType === HaulRoadLayerType.EdgesLayer && (
            <Accordion
              variant={AccordionVariant.ChevronRight}
              defaultActiveKey="optional-fields"
            >
              <Accordion.Item
                title="Optional Fields"
                eventKey="optional-fields"
              >
                <InputGroup>
                  <Input.Label info="Select a Haul Road Median layer with polygons">
                    Haul Road Median Layer
                  </Input.Label>
                  <Input.Select
                    placeholder="Select Median Layer from Workspace"
                    options={polygonLayersSelectOption}
                    value={values.haulRoadMedianLayer}
                    name={names.haulRoadMedianLayer}
                    isLoading={isLoadingPolygonFeaturesResponse}
                    menuPlacement="top"
                    onChange={(selectedLayer: SelectOption<WorkspaceLayer>) => {
                      setValues({
                        ...values,
                        haulRoadMedianLayer: selectedLayer,
                      });
                    }}
                    {...{ onBlur, onFocus }}
                  />
                </InputGroup>
              </Accordion.Item>
            </Accordion>
          )}
      </div>

      <div className="submit-btn">
        <Button
          onClick={onSubmit}
          isLoading={isAddHaulRoadPending}
          variant={ButtonVariant.Primary}
          type="submit"
          disabled={isGenerateHRADisabled}
        >
          Generate Haul Road
        </Button>
      </div>
    </form>
  ) : (
    <HRAViewOutputs
      onClickNewHaulRoad={onClickNewHaulRoad}
      onClickViewOutputs={onClickViewOutputs}
    />
  );
};
